import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useUIContext } from "@hooks";
import {
  reset,
  setUserId as setAnalyticsUserId,
  trackSignedIn,
  trackSignedUp,
} from "@utils/analytics";

// Default redirect URL on successful sign-in
const DEFAULT_SIGN_IN_REDIRECT = "/projection";
// Direct URL to sign up form, skipping "Get Started" (and description, when
// in mobile view)
const SIGN_UP_FORM_URL = "/signup?form=signup&mobile_description=false";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Used to display loading screen when signing user in
  const { setIsLoading } = useUIContext();

  // Initialise from sessionStorage if it exists; otherwise, false
  const [isSignedIn, setIsSignedIn] = useState(() => {
    const stored = sessionStorage.getItem("isSignedIn");
    return stored === "true";
  });

  // Sync to sessionStorage whenever `isSignedIn` changes
  useEffect(() => {
    sessionStorage.setItem("isSignedIn", isSignedIn);
  }, [isSignedIn]);

  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [id, setId] = useState(() => sessionStorage.getItem("userId") || "");

  // Store user entitlements, e.g., hasFullCourseAccess, etc.
  const [entitlements, setEntitlements] = useState(() => {
    const stored = sessionStorage.getItem("entitlements");
    // If undefined, return empty
    if (!stored) return {};
    // Otherwise, parse the stored value
    return JSON.parse(stored);
  });
  // Keep entitlements in a ref so memoization is not broken, as refs don't
  // trigger re-renders when updated
  const entitlementsRef = useRef(entitlements);
  // Store user entitlements in `sessionStorage` and React state
  const saveEntitlements = useCallback((ent) => {
    sessionStorage.setItem("entitlements", JSON.stringify(ent));
    setEntitlements(ent);
  }, []);

  const navigate = useNavigate();

  const setUserEmail = useCallback((email) => {
    sessionStorage.setItem("userEmail", email);
    setEmail(email);
  }, []);

  const getUserEmail = useCallback(
    () => sessionStorage.getItem("userEmail") || email,
    [email],
  );

  // Called only in `signInUser`. Sets `userId` in React state, sessionStorage,
  // and Amplitude analytics, the latter to allow the same user with multiple
  // sessions to be unified.
  const setUserId = (userId) => {
    sessionStorage.setItem("userId", userId);
    setId(userId);
    // Set up analytics session
    setAnalyticsUserId(userId);
  };

  const getUserId = () => (
    sessionStorage.getItem("userId") || id
  );

  // If the given status code is `200`, return `userId`. Otherwise, register
  // the user in the user database via a call to `onboardNewUser`, then
  // return `userId`.
  const registerUser = useCallback(async (status, body) => {
    if (status === 200) return { userId: body.userId };
    
    if (status === 404) {
      const url =
        "https://wvru9a5el6.execute-api.eu-west-1.amazonaws.com/default/onboardNewUser";

      // TEMPORARY -- Placeholder questionnaire data
      // As the questionnaire flow is deprecated, this can be removed when the
      // user database is replaced.
      const formData = {
        name: "",
        email: sessionStorage.getItem("userEmail"),
        birthYear: "",
        ethnicity: [],
        gender: "",
        priorities: [],
        attributes: [],
        motivation: "",
        financialGoals: [],
        goals: [],
      };

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(formData),
        });

        const body = await response.json();

        return { userId: body.userId };
      } catch (error) {
        console.error("Error: ", error);
      };
    };
  }, []);

  // Fetch entitlements from user database via backend Lambda
  const fetchEntitlements = useCallback(async (email) => {
    const url =
      "https://6epff086mi.execute-api.eu-west-1.amazonaws.com/default/getEntitlements";

    try {
      console.log("email: ", email)
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      // Set `entitlements` and return
      const ent = data.entitlements || {};
      saveEntitlements(ent);
      return ent;
    } catch (error) {
      console.error("Failed to fetch entitlements: ", error);
      // Set `entitlements` and return
      saveEntitlements({});
      return {};
    };
  }, [saveEntitlements]);

  // Returns the specified entitlement
  // Parameters:
  //   1. key (required) – identifier for the entitlement.
  //   2. options (optional object) – currently supports { syncWithBackend }.
  //      - Destructuring is used to extract syncWithBackend.
  //      - syncWithBackend defaults to false if not provided.
  //      - options defaults to {} so the function can be safely called with
  //        only the one argument (key).
  // --------------------------------------------------------------------------
  // Note: Previously, `getEntitlement` depended on `entitlements` which caused
  // an infinite loop when called with `syncWithBackend` as `fetchEntitlements`
  // updated `entitlements`. Thus, an entitlements ref is used since refs do
  // not trigger re-renders when updated.
  const getEntitlement = useCallback(
    async (key, { syncWithBackend = false } = {}) => {
      // Cannot sync with backend if the user is not signed in
      if (syncWithBackend && isSignedIn) {
        const ent = await fetchEntitlements(getUserEmail());
        return ent[key] || false;
      }
      return entitlementsRef.current[key] || false;
    },
    [fetchEntitlements, getUserEmail]
  );

  // Finish signing in--hide loading screen and navigate to projection page.
  // Includes a 2 second to ensure loading screen is displayed for a suitable
  // length of time.
  const finishSignIn = async (redirect = DEFAULT_SIGN_IN_REDIRECT) => {
    // Ensures loading screen is displayed long enough
    await new Promise((resolve) => setTimeout(resolve, 2000));
    // Navigate to redirect (projection page by default)
    navigate(redirect);
    // Hide loading screen
    setIsLoading(false);
  };

  // Sign in a guest
  const signInGuest = useCallback(
    async (redirect = DEFAULT_SIGN_IN_REDIRECT) => {
      // Display loading screen
      setIsLoading(true);
      await finishSignIn(redirect);
    },
    [navigate]
  );

  // Sign in (and register, if necessary) a user
  const signInUser = useCallback(
    async ({
      name, email, redirect = DEFAULT_SIGN_IN_REDIRECT, method = "",
    }) => {
      // Display loading screen
      setIsLoading(true);

      const url =
        "https://ojxoecgquj.execute-api.eu-west-1.amazonaws.com/default/signIn";

      try {
        // Returns status code `200` if the user exists in the user database,
        // and `404` if not. In the `404` case, the new user must be registered
        // by making a call to `onboardNewUser` (see `registerUser`
        // implementation).
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name, email }),
        });
        const status = response.status;
        const body = await response.json();

        // Wait for `registerUser` to return `userId` before continuing
        const { userId } = await registerUser(status, body);

        // Set user ID
        setUserId(userId);

        // (Analytics) If existing user, trigger "Signed In" event
        if (status === 200) trackSignedIn(method);
        // If new user, trigger "Signed Up" event
        if (status === 404) trackSignedUp(method);

        // Fetch user entitlements
        await fetchEntitlements(email);

        // Flag user as signed in
        setIsSignedIn(true);

        // Finish signing in
        await finishSignIn(redirect);
      } catch (error) {
        console.error("Error fetching profile data: ", error);
      } finally {
        // Hide loading screen
        setIsLoading(false);
      };
    },
    [fetchEntitlements, registerUser, navigate]
  );

  // Sign out current user
  const signOutUser = async () => {
    // Display loading screen
    setIsLoading(true);
    // Clear app session
    sessionStorage.clear();
    setEmail("");
    // Reset analytics session
    reset();
    // Navigate to `/signout` page
    navigate("/signout?status=signed_out");
    // Ensures loading screen is displayed long enough
    await new Promise((resolve) => setTimeout(resolve, 2000));
    // Reload the page; note that this implicitly hides the loading screen
    window.location.reload();
  };

  const contextValue = {
    SIGN_UP_FORM_URL,
    isSignedIn,
    setIsSignedIn,
    name,
    setName,
    givenName,
    setGivenName,
    familyName,
    setFamilyName,
    email,
    setUserEmail,
    getUserEmail,
    getUserId,
    getEntitlement,
    signInGuest,
    signInUser,
    signOutUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export default AuthContext;
